import { darken, type Theme } from "@mui/material/styles";

/** Shared by outlined filter triggers (legacy + label+summary) — avoids split px/py fighting MUI defaults. */
export const FILTER_TRIGGER_OUTLINED_SX = {
  padding: "4px 9px",
  fontSize: "0.8125rem",
} as const;

/** External filter-bar labels (e.g. "States:", "Quantity:"). */
export const FILTER_BAR_LABEL_FONT_SIZE_PX = 9;
export const FILTER_BAR_LABEL_FONT_WEIGHT = 600;

/** Outlined value triggers next to those labels ("Any", summaries). */
export const FILTER_VALUE_TRIGGER_HEIGHT_PX = 32;
export const FILTER_VALUE_TRIGGER_FONT_SIZE_PX = 9.75;
export const FILTER_VALUE_TRIGGER_FONT_WEIGHT = 500;
export const FILTER_VALUE_TRIGGER_BORDER_RADIUS_PX = 6;
export const FILTER_VALUE_TRIGGER_ICON_SIZE_PX = 10;
export const FILTER_VALUE_TRIGGER_ICON_GAP_PX = 4;
/** Start-icon size for toolbar actions that reuse the filter-trigger look (e.g. Saved Searches). */
export const FILTER_VALUE_TRIGGER_START_ICON_SIZE_PX = 12;

interface FilterValueTriggerButtonSxOptions {
  /** When set, styles the start icon (left of label). */
  startIconSizePx?: number;
  /** End-icon size; defaults to the filter-trigger chevron size. */
  endIconSizePx?: number;
  minWidth?: number;
}

/**
 * Shared outlined trigger look for filter-bar value buttons ("Any") and
 * matching toolbar actions (Saved Searches).
 */
export function filterValueTriggerButtonSx(
  theme: Theme,
  options: Readonly<FilterValueTriggerButtonSxOptions> = {},
) {
  const { filterTriggerTextColor, filterTriggerBorderColor } = theme.app.filterBar;
  const endIconSize = options.endIconSizePx ?? FILTER_VALUE_TRIGGER_ICON_SIZE_PX;
  const startIconSize = options.startIconSizePx;
  const minWidth = options.minWidth ?? 56;

  return {
    textTransform: "none",
    fontSize: FILTER_VALUE_TRIGGER_FONT_SIZE_PX,
    fontWeight: FILTER_VALUE_TRIGGER_FONT_WEIGHT,
    color: filterTriggerTextColor,
    borderColor: filterTriggerBorderColor,
    borderRadius: `${FILTER_VALUE_TRIGGER_BORDER_RADIUS_PX}px`,
    boxSizing: "border-box",
    height: FILTER_VALUE_TRIGGER_HEIGHT_PX,
    minHeight: FILTER_VALUE_TRIGGER_HEIGHT_PX,
    maxHeight: FILTER_VALUE_TRIGGER_HEIGHT_PX,
    padding: "0 9px",
    minWidth,
    "&:hover": { borderColor: filterTriggerBorderColor },
    ...(startIconSize != null
      ? {
          "& .MuiButton-startIcon": {
            ml: 0,
            mr: `${FILTER_VALUE_TRIGGER_ICON_GAP_PX}px`,
            color: filterTriggerTextColor,
            "& > *:nth-of-type(1)": {
              fontSize: startIconSize,
              width: startIconSize,
              height: startIconSize,
            },
          },
        }
      : {}),
    "& .MuiButton-endIcon": {
      ml: `${FILTER_VALUE_TRIGGER_ICON_GAP_PX}px`,
      color: filterTriggerTextColor,
      "& > *:nth-of-type(1)": {
        fontSize: endIconSize,
        width: endIconSize,
        height: endIconSize,
      },
    },
  } as const;
}

/** Contained filter action height for Apply inside popovers. */
export const FILTER_APPLY_BUTTON_HEIGHT_PX = 24;

interface FilterFilledActionButtonSxOptions {
  /** Defaults to the shared filter-trigger height (32). Apply uses 24. */
  heightPx?: number;
  /** Soft elevation from `theme.shadows` (Apply). Advanced stays flat. */
  elevated?: boolean;
}

/** Contained filter action (Apply in popovers, Advanced on the filter bar). */
export function filterFilledActionButtonSx(
  theme: Theme,
  options: Readonly<FilterFilledActionButtonSxOptions> = {},
) {
  const { filledActionBackgroundColor, advancedButtonTextColor } = theme.app.filterBar;
  const height = options.heightPx ?? FILTER_VALUE_TRIGGER_HEIGHT_PX;
  const elevated = options.elevated === true;
  const boxShadow = elevated ? theme.shadows[2] : "none";

  return {
    textTransform: "none",
    fontSize: FILTER_VALUE_TRIGGER_FONT_SIZE_PX,
    fontWeight: FILTER_VALUE_TRIGGER_FONT_WEIGHT,
    color: advancedButtonTextColor,
    backgroundColor: filledActionBackgroundColor,
    borderRadius: `${FILTER_VALUE_TRIGGER_BORDER_RADIUS_PX}px`,
    boxSizing: "border-box",
    height,
    minHeight: height,
    maxHeight: height,
    padding: "0 9px",
    boxShadow,
    "&:hover": {
      backgroundColor: darken(filledActionBackgroundColor, 0.12),
      boxShadow: elevated ? theme.shadows[4] : "none",
    },
  } as const;
}
